import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePublicSession } from '../contexts/PublicSessionContext';
import { prepareOAuthRedirect, clearAuthRedirectFlags } from '../utils/authFlow';
import useSEO from '../hooks/useSEO';
import MarketingLayout from '../components/MarketingLayout';
import ApWordmark from './platform/ApWordmark';
import './platform/platform.css';
import './auth-transition-folio.css';

const PostPaymentSignup = () => {
  const [subscriptionType, setSubscriptionType] = useState('basic');
  const [error, setError] = useState('');
  const [debug, setDebug] = useState({});
  const [googleLoading, setGoogleLoading] = useState(false);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const { signInWithGoogle } = usePublicSession();
  const navigate = useNavigate();
  const location = useLocation();

  useSEO({
    title: 'Complete registration | APDoneRight',
    description: 'Finish signing in to activate your APDoneRight subscription.',
    canonical: '/post-payment-signup',
    robots: 'noindex,follow',
  });

  // Check if user is coming from payment
  useEffect(() => {
    const paymentComplete = localStorage.getItem('paymentCompleted');
    const storedSubscriptionType = localStorage.getItem('subscriptionType');

    console.log('PostPaymentSignup: Checking payment status', {
      paymentComplete,
      storedSubscriptionType
    });

    setDebug({ paymentComplete, storedSubscriptionType });

    if (paymentComplete === 'true') {
      setPaymentCompleted(true);
      if (storedSubscriptionType) {
        setSubscriptionType(storedSubscriptionType);
        console.log(`PostPaymentSignup: Setting subscription type to ${storedSubscriptionType}`);
      } else {
        console.warn('PostPaymentSignup: Payment completed but no subscription type found');
      }
    } else {
      console.log('PostPaymentSignup: No payment completion found in localStorage');
    }

    // Check URL for email parameter
    const searchParams = new URLSearchParams(location.search);
    const email = searchParams.get('email');
    if (email) {
      console.log(`PostPaymentSignup: Email found in URL: ${email}`);
      setDebug(prev => ({ ...prev, emailFromUrl: email }));
    }
  }, [location.search]);

  const handleGoogleSignIn = async () => {
    try {
      setError('');
      setGoogleLoading(true);

      console.log(`PostPaymentSignup: Initiating Google sign in...`);
      setDebug(prev => ({ ...prev, signingInWith: subscriptionType }));

      const authPrep = await prepareOAuthRedirect();
      if (!authPrep.ok) {
        setError('Authentication in progress in another tab. Please wait...');
        setGoogleLoading(false);
        return;
      }

      // Sign in with Google OAuth
      await signInWithGoogle({ forceAccountSelection: true });
      console.log('PostPaymentSignup: Redirecting to Google...');

    } catch (err) {
      console.error('PostPaymentSignup: Sign in error', err);
      setDebug(prev => ({ ...prev, signInError: err?.message }));
      setError(err?.message || 'Failed to initialize Google sign in');
      setGoogleLoading(false);
      clearAuthRedirectFlags();
    }
  };

  return (
    <MarketingLayout
      showNavbar={false}
      showFooter={false}
      className="ap-auth-transition text-slate-900 font-sans"
      mainClassName="flex flex-col"
    >
      <div className="relative flex-1 flex items-center justify-center overflow-hidden">
        <div className="max-w-md w-full space-y-8 relative z-10 px-4">
          <header className="absolute top-0 left-0 w-full p-6">
            <ApWordmark />
          </header>

          <div className="ap-auth-transition-card bg-white border border-slate-100 rounded-3xl p-10 shadow-[0_8px_30px_rgb(0,0,0,0.04)] relative z-10 transition-colors">
            <div className="w-full">
              <div className="ap-transition-icon mx-auto flex items-center justify-center bg-emerald-50 mb-6 w-16 h-16 shadow-sm border border-emerald-100 rounded-2xl">
                <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-center text-3xl font-bold text-slate-900 tracking-tight mb-4">
                Payment Verified
              </h2>
              <p className="mt-2 text-center text-sm text-slate-500 font-medium mb-8">
                {paymentCompleted
                  ? `Your payment was successful. Please sign in to access your ${subscriptionType} features.`
                  : "Please sign in with Google to continue."}
              </p>

              {error && (
                <div className="bg-red-50 border border-red-100 text-red-600 p-4 rounded-2xl mb-6 text-sm font-medium">
                  {error}
                </div>
              )}

              {/* Google Sign In Button */}
              <div className="mb-2">
                <button
                  type="button"
                  onClick={handleGoogleSignIn}
                  disabled={googleLoading}
                  className="group relative w-full flex items-center justify-center py-3.5 px-6 bg-white text-slate-700 font-semibold border border-slate-200 hover:bg-slate-50 hover:border-slate-300 transition-all shadow-sm rounded-full disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {googleLoading ? (
                    <div className="flex items-center gap-3">
                      <svg className="animate-spin h-5 w-5 text-slate-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Connecting...</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 w-full justify-center">
                      <div className="flex items-center justify-center">
                        <svg className="w-4 h-4" viewBox="0 0 24 24">
                          <path
                            fill="#4285F4"
                            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                          />
                          <path
                            fill="#34A853"
                            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                          />
                          <path
                            fill="#FBBC05"
                            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                          />
                          <path
                            fill="#EA4335"
                            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                          />
                          <path fill="none" d="M1 1h22v22H1z" />
                        </svg>
                      </div>
                      <span>Continue with Google</span>
                    </div>
                  )}
                </button>
              </div>

              <p className="mt-8 text-xs text-center text-slate-400 font-medium">
                By continuing, you agree to our Terms of Service and Privacy Policy.
              </p>

              {process.env.NODE_ENV === 'development' && (
                <div className="mt-8 p-4 border border-gray-200 rounded-md bg-gray-50">
                  <h4 className="text-sm font-medium text-gray-500">Debug Information:</h4>
                  <pre className="mt-2 text-xs text-gray-500 overflow-auto max-h-40">{JSON.stringify(debug, null, 2)}</pre>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </MarketingLayout >
  );
};

export default PostPaymentSignup;
